// pages/SoftBinReportPage.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging,
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const SOFT_BIN_API_URL = `${API_URL}/api/oracle/soft-bin-summary`;
//const CUST_API_URL = `${API_URL}/api/oracle/common/custs`;
//const DEVICE_API_URL = `${API_URL}/api/oracle/common/devicesALL`;

const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;



const processRawData = (rawData) => {
  if (!rawData || rawData.length === 0) {
    return { pivotedData: [], sbinColumns: [] };
  }
  const grouped = new Map();
  const sbinSet = new Set();
  rawData.forEach(row => {
    const key = `${row.LOT_ID}|${row.OPER}|${row.TEST_MODE}`;
    if (!grouped.has(key)) {
      grouped.set(key, { ...row, SBINS: {} });
    }
    const entry = grouped.get(key);
    if (row.SBIN_NAME && row.BIN_QTY !== null) {
      const sbinKey = `SBIN${row.SBIN_NAME}`;
      entry.SBINS[sbinKey] = row.BIN_QTY;
      sbinSet.add(sbinKey);
    }
  });
  const sbinColumns = Array.from(sbinSet).sort((a, b) => parseInt(a.substring(4), 10) - parseInt(b.substring(4), 10));
  const pivotedData = Array.from(grouped.values()).map(item => {
    const finalRow = { ...item };
    sbinColumns.forEach(colName => {
      finalRow[colName] = item.SBINS[colName] || 0;
    });
    const goodQty = item.SBINS['SBIN1'] || 0;
    const totalQty = Object.values(item.SBINS).reduce((sum, qty) => sum + qty, 0);
    const failQty = totalQty - goodQty;
    const yld = totalQty > 0 ? ((goodQty / totalQty) * 100).toFixed(2) : '0.00';
    finalRow.TOTAL_QTY = totalQty;
    finalRow.FAIL_QTY = failQty;
    finalRow.YLD = yld;
    delete finalRow.SBINS;
    delete finalRow.SBIN_NAME;
    delete finalRow.BIN_QTY;
    return finalRow;
  });
  return { pivotedData, sbinColumns };
};

export default function SoftBinReportPage({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [sBinColumns, setSBinColumns] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [lotId, setLotId] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);

  const formatDate = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } }).then(res => setCustList(res.data.data || [])).catch(console.error);
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]); setSelectedDevice('ALL'); return;
    }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
    .then(res => { 
      const fetchedDevices = res.data.data || [];
      // 'ALL' 옵션 객체를 생성합니다.
      const allOption = { CODE_CD: 'ALL' };
      // 받아온 목록 맨 앞에 'ALL' 옵션을 추가하여 상태를 업데이트합니다.
      setDeviceList([allOption, ...fetchedDevices]);
      setSelectedDevice('ALL'); })
    .catch(console.error);
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);

  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    setSBinColumns([]);

    const params = {
      factory: selectedFactory, custCd: selectedCust, device: selectedDevice,
      lotId: lotId.trim() || 'ALL', dateFrom: formatDate(dateFrom), dateTo: formatDate(dateTo),
    };
    
    axios.get(SOFT_BIN_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        const { pivotedData, sbinColumns } = processRawData(res.data.data);
        setSBinColumns(sbinColumns.map(colName => ({
            dataField: colName,
            caption: colName.replace('SBIN', 'Sbin'),
        })));
        setData(pivotedData.map((row, idx) => ({ ...row, id: idx })));
      })
      .catch(e => { setError(`조회 오류: ${e.response?.data?.retMsg || e.message}`); setData([]); })
      .finally(() => setLoading(false));
  }, [selectedFactory, selectedCust, selectedDevice, lotId, dateFrom, dateTo]);

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Soft_Bin_Report");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Soft_Bin_Report.xlsx"));
    e.cancel = true;
  };
  
  return (
    <div style={{ padding: 20, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 (높이는 내용에 따라 자동 결정) */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Soft Bin Summary</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['FT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <TextBox value={lotId} onValueChanged={e => setLotId(e.value)} placeholder="LOT ID (ALL)" label="LOT ID" width={180} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 (남은 모든 세로 공간을 차지하도록 설정) */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          columnAutoWidth={true} 
          columnVirtualization={true}
          showBorders={true}
          allowColumnResizing={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={10} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} />
          <Sorting mode="multiple" />
    
          <Column dataField="FACTORY" caption="Factory" fixed={true} width={100} />
          <Column dataField="CUSTOMER" caption="Customer" fixed={true} width={120} />
          <Column dataField="DEVICE" caption="Device" fixed={true} width={150} />
          <Column dataField="OPER" caption="Oper" fixed={true} width={100} />
          <Column dataField="PROGRAM" caption="Program" width={200} />
          <Column dataField="TESTER_ID" caption="Tester ID" width={120} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} />
          <Column dataField="TEST_MODE" caption="TEST MODE" width={150} />
          <Column 
            dataField="START_TIME" 
            caption="Start Time" 
            width={200} 
            dataType="datetime" 
            format="yyyy-MM-dd HH:mm:ss" 
          />
          <Column 
            dataField="END_TIME" 
            caption="End Time" 
            width={200} 
            dataType="datetime" 
            format="yyyy-MM-dd HH:mm:ss" 
          />
        
          <Column dataField="TOTAL_QTY" caption="Total Qty" dataType="number" alignment="center" width={100} />
          <Column dataField="FAIL_QTY" caption="Fail Qty" dataType="number" alignment="center" width={100} />
          <Column dataField="YLD" caption="Yld" alignment="right" width={80} />
          {sBinColumns.map(col => (
            <Column
              key={col.dataField}
              dataField={col.dataField}
              caption={col.caption}
              dataType="number"
              alignment="center"
              minWidth={60} 
            />
          ))}
        </DataGrid>
      </div>
    </div>
  );
}